'use client';

import { type RefObject, useEffect } from 'react';

// How long the visual viewport must hold still before we treat the keyboard
// animation as finished. Comfortably longer than the gap between consecutive
// resize events in a single animation, comfortably shorter than the animation
// itself.
const VIEWPORT_QUIET_MS = 120;

// Deadline for platforms that never fire a visualViewport resize at all — no
// software keyboard, a keyboard that was already up, or an Android WebView
// that resizes the layout viewport instead. Long enough to clear iOS Safari's
// own focus scroll, short enough not to read as lag.
const NO_RESIZE_FALLBACK_MS = 300;

/**
 * Scrolls `ref` to the top of the viewport once, each time `active` flips true.
 *
 * Mobile Safari runs its own scroll when an input takes focus and *then*
 * shrinks the visual viewport as the keyboard animates in, so a scroll issued
 * during the focus handler measures geometry that is about to change. The
 * keyboard emits a burst of resize events across that animation, and only the
 * last one describes the settled viewport — measuring on the first would land
 * mid-animation and then lose to Safari's own scroll-into-view. So each resize
 * pushes the measurement out by a quiet window, and we only scroll once the
 * bursts stop.
 *
 * Breathing room above the element is the caller's to set, as `scrollMarginTop`
 * on the target — `scrollIntoView` honors it, so the gutter stays a Panda token
 * next to the padding it lines up with instead of a number in here.
 */
export function useScrollToViewportTop(ref: RefObject<HTMLElement | null>, active: boolean) {
  useEffect(() => {
    if (!active) return;

    const viewport = window.visualViewport ?? null;
    let timeoutId: ReturnType<typeof setTimeout>;
    let scrolled = false;

    const scrollToTop = () => {
      const element = ref.current;
      // Not latched: a caller that mounts its target as the panel opens should
      // still get scrolled by a later resize.
      if (!element) return;

      scrolled = true;
      element.scrollIntoView({
        block: 'start',
        inline: 'nearest',
        behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth',
      });
    };

    const scheduleScroll = (delay: number) => {
      if (scrolled) return;
      clearTimeout(timeoutId);
      timeoutId = setTimeout(scrollToTop, delay);
    };

    const handleResize = () => scheduleScroll(VIEWPORT_QUIET_MS);

    scheduleScroll(NO_RESIZE_FALLBACK_MS);
    viewport?.addEventListener('resize', handleResize);

    return () => {
      clearTimeout(timeoutId);
      viewport?.removeEventListener('resize', handleResize);
    };
  }, [active, ref]);
}
